/**
 * Checking waterfall — cheap → expensive discharge of one receipt's claims.
 *
 * Renders the data-layer `CheckingWaterfall` DTO. The probe in
 * `scripts/rendered-output-probes.mjs` (`verification-waterfall`) asserts
 * each row's `tierLabel` against the data layer — do not hardcode a tier.
 * MUTATION ANCHOR: replacing `row.tierLabel` with a literal ("T2 mechanical")
 * turns that probe RED.
 */

import {
  DataTable,
  type DataTableColumn,
} from "../data-table";
import { StatusBadge } from "../status-badge";
import type { EvidenceWaterfallRowDto } from "../../api/contracts/evidence";
import type { StatusOf } from "../../tokens";

const MUTED = "type-subtext text-muted-foreground";

type TierStatus = StatusOf<"verification_tier">;

function tierStatus(tier: number | null): TierStatus {
  if (tier === 0) return "t0";
  if (tier === 1) return "t1";
  if (tier === 2) return "t2";
  if (tier === 3) return "t3";
  if (tier === 4) return "t4";
  return "undischarged";
}

const COLUMNS: DataTableColumn<EvidenceWaterfallRowDto>[] = [
  {
    key: "label",
    header: "Claim",
    render: (row) => (
      <span className="type-body line-clamp-2" title={row.label}>
        {row.label}
      </span>
    ),
  },
  {
    key: "tier",
    header: "Tier",
    render: (row) => (
      <StatusBadge
        domain="verification_tier"
        status={tierStatus(row.tier)}
        label={row.tierLabel}
        withDot
      />
    ),
  },
  {
    key: "cost",
    header: "Cost",
    render: (row) => <span className={MUTED}>{row.cost}</span>,
  },
  {
    key: "outcome",
    header: "Outcome",
    render: (row) => (
      <StatusBadge
        domain="verification_tier"
        status={tierStatus(row.outcome === "undischarged" ? null : row.tier)}
        label={row.outcome}
      />
    ),
  },
];

export function CheckingWaterfallTable({
  rows,
}: {
  rows: EvidenceWaterfallRowDto[];
}) {
  if (rows.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No claims or gate receipts to place on the waterfall yet.
      </p>
    );
  }
  return (
    <DataTable
      rows={rows}
      columns={COLUMNS}
      rowKey={(r) => r.key}
      wrapInCard={false}
    />
  );
}
